import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import {
  remoteConnectDetailsQuery,
  remoteConnectJournalQuery,
  remoteConnectPermissionsQuery,
  remoteConnectTabsQuery,
  saveRemoteConnectPermissions,
  type RemoteConnectPermissionsPage,
  type RemoteConnectTab,
} from '../api/remoteConnectDevice'
import { CheckField, FormRow, HalfField } from '../ui/FormGrid'
import { toast } from '../ui/notify'
import { SelectPicker } from '../ui/SelectPicker'
import { Spinner } from '../ui/Spinner'
import './RemoteConnectTabs.css'

/**
 * Randul desfacut din lista Remote Connect - `Remote_connect::getRemoteTabs` si
 * cele trei file ale lui, cu ACELASI markup ca machetele
 * `views/version-3/remote_connect/{actionsTabs,details,journal,settings}.php`:
 *
 *   Details             deviceDetails         rubricile din `mlist-details`
 *   Session journal     sessionsJournal       tabelul `rc-journal`, fisierele sub rand
 *   Remote Permissions  requestDisableConsent formularul "Remote Access" (update_access)
 *
 * Care file se vad hotaraste serverul (drepturi + unealta): jurnalul exista
 * numai pe RustDesk, unde evidenta sesiunilor e la noi.
 *
 * Fila e o pereche `ul.action_tabs li a.action_button` + `.details_content`,
 * ca in `actionsTabs.php`; prima fila se deschide singura, cum facea
 * `ExpandRow` in portal.
 */

const DASH = '—'

export function RemoteConnectTabs({ id }: { id: number }) {
  const page = useQuery(remoteConnectTabsQuery(id))
  const [tab, setTab] = useState<RemoteConnectTab['key'] | null>(null)
  const current = tab ?? page.data?.tabs[0]?.key ?? null

  if (page.error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {(page.error as Error).message}
      </div>
    )
  }
  if (!page.data) return <Spinner />

  return (
    <div className="row d-flex m-0 rc-row" data-device={id}>
      <div className="col-auto bg-lightest-gray">
        <ul className="action_tabs">
          {page.data.tabs.map((entry) => (
            <li key={entry.key}>
              <a
                href={`/app/${entry.href}`}
                className={current === entry.key ? 'action_button active' : 'action_button'}
                onClick={(e) => {
                  e.preventDefault()
                  setTab(entry.key)
                }}
              >
                {entry.label}
              </a>
            </li>
          ))}
        </ul>
      </div>
      <div className="col-md-12">
        <div className="details_content">
          {current === 'details' && <DetailsTab id={id} />}
          {current === 'journal' && <JournalTab id={id} />}
          {current === 'permissions' && <PermissionsTab id={id} />}
          {current === null && (
            <div className="alert alert-info" role="alert">
              <i className="fas fa-info-circle" aria-hidden="true"></i> You do not have access to any of the remote
              connect panels.
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Details (details.php)                                                    */
/* ------------------------------------------------------------------------ */

function DetailsTab({ id }: { id: number }) {
  const { data, error } = useQuery(remoteConnectDetailsQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {(error as Error).message}
      </div>
    )
  }
  if (!data) return <Spinner />

  const summary = data.summary

  return (
    <div className="rc-details">
      <div className="notifications-box"></div>
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Device</strong> remote control details
        </h4>
      </header>
      <div className="row row-content rc-details-body">
        <div className="col-md-6">
          <table className="mlist-details" width="100%">
            <tbody>
              {data.tool !== 'rustdesk' && (
                <tr>
                  <td>
                    <b>Consent status: </b>
                  </td>
                  <td>
                    <label className="label label-danger">Disabled</label>
                  </td>
                </tr>
              )}
              <tr>
                <td>
                  <b>Last connection: </b>
                </td>
                <td>{summary?.lastStarted ?? ''}</td>
              </tr>
              <tr>
                <td>
                  <b>Last activity: </b>
                </td>
                <td>
                  {summary && summary.lastStarted !== null ? (
                    summary.inProgress ? (
                      <span className="label label-success">session in progress</span>
                    ) : (
                      summary.lastEnded
                    )
                  ) : (
                    ''
                  )}
                </td>
              </tr>
              <tr>
                <td>
                  <b>Last user connected: </b>
                </td>
                <td>{summary?.lastOperator ?? ''}</td>
              </tr>
              <tr>
                <td>
                  <b>Total connections: </b>
                </td>
                <td>{summary ? summary.total : ''}</td>
              </tr>
              {summary && summary.totalSeconds > 0 && (
                <tr>
                  <td>
                    <b>Total time connected: </b>
                  </td>
                  <td>{summary.totalText}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}

/* ------------------------------------------------------------------------ */
/*  Session journal (journal.php)                                            */
/* ------------------------------------------------------------------------ */

function JournalTab({ id }: { id: number }) {
  const { data, error } = useQuery(remoteConnectJournalQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {(error as Error).message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return (
    <div className="rc-journal-wrap">
      <header className="panel-heading sm" data-color="theme-inverse">
        <h4>
          <strong>Session</strong> journal
        </h4>
      </header>
      <div className="row row-content rc-journal-body">
        <div className="col-md-12">
          {data.sessions.length === 0 ? (
            <div className="rc-journal-empty">
              <i className="fas fa-plug" aria-hidden="true"></i> No sessions recorded yet. The journal starts with the
              first RustDesk connection made through the portal.
            </div>
          ) : (
            <>
              <table className="table rc-journal">
                <thead>
                  <tr>
                    <th>Started</th>
                    <th>Duration</th>
                    <th>Operator</th>
                    <th>Account</th>
                    <th>Ticket</th>
                    <th className="rc-num">Files moved</th>
                  </tr>
                </thead>
                <tbody>
                  {data.sessions.map((session) => (
                    <SessionRows key={session.id} session={session} />
                  ))}
                </tbody>
              </table>
              {data.truncated && (
                /* Un jurnal care tace despre ce a taiat se citeste ca intreg -
                   si la un audit exact asta nu are voie sa se intample. */
                <p className="rc-muted rc-journal-note">
                  <i className="fas fa-info-circle" aria-hidden="true"></i> Only the newest {data.limit} sessions are
                  shown; older history exists but is not on this screen.
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  )
}

function SessionRows({ session }: { session: import('../api/remoteConnectDevice').RemoteConnectJournalSession }) {
  return (
    <>
      <tr>
        <td className="rc-nowrap">
          {session.startedAt}
          {session.stealth && (
            <span
              className="label label-warning rc-stealth"
              title="Connected without the person at the desk being shown anything"
            >
              stealth
            </span>
          )}
        </td>
        <td className="rc-nowrap">
          {session.inProgress ? (
            <span className="label label-success">in progress</span>
          ) : (
            <>
              {session.durationText}
              {session.presumed && (
                <span
                  className="rc-muted rc-presumed"
                  title="The agent never reported the end; a cron closed the session and the duration is an assumption, not a fact"
                >
                  (presumed)
                </span>
              )}
            </>
          )}
        </td>
        <td>{session.operator}</td>
        <td>{session.account}</td>
        <td className="rc-nowrap">
          {session.ticket !== null ? (
            <a href={`/app/easeedesk/ticket/${session.ticket}`} target="_blank" rel="noopener">
              #{session.ticket}
            </a>
          ) : (
            <span className="rc-muted">{DASH}</span>
          )}
        </td>
        <td className="rc-num">{session.files.length > 0 ? session.files.length : <span className="rc-muted">0</span>}</td>
      </tr>
      {session.files.length > 0 && (
        /* Fisierele sesiunii, sub randul ei. Directia se spune in cuvinte, nu
           doar cu o sageata: "taken from device" e exact ce cauta un control. */
        <tr>
          <td colSpan={6} className="rc-files-cell">
            <table className="rc-journal-files">
              <tbody>
                {session.files.map((file, i) => (
                  <tr key={i}>
                    <td className="rc-nowrap rc-file-dir">
                      {file.takenFromDevice ? (
                        <>
                          <i className="fas fa-arrow-up rc-dir-up" aria-hidden="true"></i> taken from device
                        </>
                      ) : (
                        <>
                          <i className="fas fa-arrow-down rc-dir-down" aria-hidden="true"></i> sent to device
                        </>
                      )}
                    </td>
                    <td className="rc-file-name">{file.name}</td>
                    <td className="rc-nowrap rc-muted rc-file-size">{file.bytesText}</td>
                    <td className="rc-nowrap rc-muted rc-file-when">{file.happenedAt}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </td>
        </tr>
      )}
    </>
  )
}

/* ------------------------------------------------------------------------ */
/*  Remote Permissions (settings.php)                                        */
/* ------------------------------------------------------------------------ */

function PermissionsTab({ id }: { id: number }) {
  const { data, error } = useQuery(remoteConnectPermissionsQuery(id))

  if (error) {
    return (
      <div className="alert alert-danger" role="alert">
        Something went wrong: {(error as Error).message}
      </div>
    )
  }
  if (!data) return <Spinner />

  return <PermissionsForm id={id} page={data} />
}

function PermissionsForm({ id, page }: { id: number; page: RemoteConnectPermissionsPage }) {
  const client = useQueryClient()
  const [access, setAccess] = useState(page.remoteConnectAccess)
  const [consent, setConsent] = useState<'inherit' | 'ask' | 'direct'>(page.rustdesk?.consent ?? 'inherit')

  useEffect(() => {
    setAccess(page.remoteConnectAccess)
    setConsent(page.rustdesk?.consent ?? 'inherit')
  }, [page])

  const save = useMutation({
    mutationFn: () =>
      saveRemoteConnectPermissions(id, {
        remote_connect_access: access,
        ...(page.rustdesk ? { rustdesk_consent: consent } : {}),
      }),
    onSuccess: async () => {
      toast('Settings saved', 'Remote Connect')
      await client.invalidateQueries({ queryKey: ['remote-connect', 'device', id] })
    },
    onError: (err: Error) => toast(err.message, 'Remote Connect', 'error'),
  })

  const rustdesk = page.rustdesk

  return (
    <div className="rc-permissions">
      <div className="notifications-box"></div>
      <div className="row">
        <div className="col-md-6">
          <header className="panel-heading sm" data-color="theme-inverse">
            <h4>
              <strong>Remote</strong> Access
            </h4>
          </header>
          <div className="panel">
            <div className="panel-body">
              <form
                method="post"
                action={`/app/remote_connect/update_access`}
                id="remote-access-settings"
                onSubmit={(e) => {
                  e.preventDefault()
                  save.mutate()
                }}
              >
                <input type="hidden" name="device_id" value={id} />
                <FormRow>
                  <CheckField
                    id={`rc-access-${id}`}
                    className="icheck"
                    name="remote_connect_access"
                    value="on"
                    label="Enable remote access"
                    checked={access}
                    onChange={setAccess}
                    span={12}
                  />
                </FormRow>

                {rustdesk && (
                  <FormRow>
                    <HalfField
                      label="Ask the person before connecting"
                      htmlFor={`rc-consent-${id}`}
                      span={12}
                      help={
                        <>
                          Applied on the station by the agent, at its next start. With &quot;Ask&quot;, an unattended
                          station cannot be reached &mdash; there is nobody to click Accept.
                        </>
                      }
                    >
                      <SelectPicker
                        id={`rc-consent-${id}`}
                        name="rustdesk_consent"
                        className="form-control"
                        title="Company default"
                        value={consent}
                        onChange={(value) => setConsent(value as 'inherit' | 'ask' | 'direct')}
                        options={[
                          { value: 'inherit', label: `Company default (${rustdesk.companyAsks ? 'ask' : 'connect directly'})` },
                          { value: 'ask', label: 'Ask — the person must click Accept; the password no longer opens the session' },
                          { value: 'direct', label: 'Connect directly with the password, without asking' },
                        ]}
                      />
                    </HalfField>
                  </FormRow>
                )}

                <div className="form-group">
                  <button className="btn btn-success btn-block" type="submit" disabled={save.isPending}>
                    Save
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>

        {page.screenconnect && (
          /*
           * Jumatatea ScreenConnect. In portal, cu `firma.disable_remote_consent`
           * aprinsa, aici era formularul "Request disable consent" catre CNR
           * (`Remote_connect::disableConsent`). Emailul pe care il trimitea avea
           * linkul de aprobare `optimumdesk/disable_consent/<key>` - iar metoda
           * aia NU EXISTA in portal: cererea se scria si nu putea fi aprobata de
           * nimeni. Un flux care nu se poate incheia nu se reface; se spune.
           */
          <div className="col-md-6">
            <header className="panel-heading sm" data-color="theme-inverse">
              <h4>
                <strong>Request</strong> disable consent
              </h4>
            </header>
            {page.screenconnect.companyDisableRemoteConsent ? (
              <div className="panel-body">
                {page.screenconnect.disabledSecondsLeft > 0 && (
                  <div className="alert alert-info" role="alert">
                    <i className="fas fa-info-circle" aria-hidden="true"></i> Consent is currently disabled on this device
                    for another {Math.ceil(page.screenconnect.disabledSecondsLeft / 60)} minutes.
                  </div>
                )}
                <div className="alert alert-warning rc-cnr-note" role="alert">
                  <i className="fas fa-exclamation-triangle" aria-hidden="true"></i> Requests to the Critical Notification
                  Receiver are not available: the approval link the portal used to e-mail
                  (<code>optimumdesk/disable_consent</code>) leads to a page that does not exist, so a request could never
                  be approved.
                </div>
              </div>
            ) : (
              <div className="alert alert-warning">Remote consent is enabled for this company.</div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
